import { Stack, useMediaQuery } from '@mui/material'
import { GeoLoc } from 'instantsearch.js'
import { useTranslation } from 'next-i18next'
import {
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import { useClearRefinements } from 'react-instantsearch'
import ReactMapGL, { MapRef, Marker } from 'react-map-gl'
import ArrowLeft from 'assets/icons/nucleo/18px_arrow-left.svg'
import Expand from 'assets/icons/nucleo/18px_arrows-expand-diagonal-2.svg'
import Reduce from 'assets/icons/nucleo/18px_arrows-reduce-diagonal-2.svg'
import Crosshairs from 'assets/icons/nucleo/18px_crosshairs.svg'
import Minus from 'assets/icons/nucleo/18px_minus.svg'
import Plus from 'assets/icons/nucleo/18px_plus.svg'
import { SearchBar } from 'components/Layout/Header/SearchBar/SearchBar'
import { Button, ButtonVariant } from 'components/common/Button'
import { NewFlexContainer } from 'components/common/common.style'
import { NEXT_PUBLIC_MAPBOX_TOKEN } from 'containers/event/map/map-env'
import { Filters } from 'containers/search/Filters/Filters'
import { SortBy } from 'containers/search/SortBy'
import { flattenObject } from 'helpers/records'
import { AutocompleteItem } from 'interfaces/algolia'
import { unifiedColors } from 'styles/colors'
import { themeUtils } from 'styles/theme'
import { EventPopup } from './EventPopup'
import { EventsDrawer } from './EventsDrawer'
import { EventsInfiniteScroll } from './EventsInfiniteScroll'
import {
  CrosshairsButton,
  ExpandButton,
  FiltersContainer,
  HideMapButton,
  MapContainer,
  SearchInZoneButtonContainer,
  Selected,
  ZoomInButton,
  ZoomOutButton
} from './SearchMap.style'
import { calculateMapBounds } from './SearchStaticMap'
import { useLanguageSetter } from './useLanguageSetter'
import { usePopupHandler } from './usePopupHandler'
import 'mapbox-gl/dist/mapbox-gl.css'

export interface Bounds {
  northEast: GeoLoc
  southWest: GeoLoc
}
interface Props {
  geoJson: GeoJSON.FeatureCollection<GeoJSON.Point, AutocompleteItem>
  onLoad: () => void
  highLightEvent?: (eventId: number | null) => void
  selectedEventId?: number | null
  refineGeoLocation: (bounds: Bounds) => void
  setExpandedMap: Dispatch<SetStateAction<boolean>>
  expandedMap: boolean
  hideMap: () => void
}

export default function SearchMap({
  geoJson,
  onLoad,
  highLightEvent,
  selectedEventId,
  refineGeoLocation,
  setExpandedMap,
  expandedMap,
  hideMap
}: Props) {
  const { t } = useTranslation(['event', 'common'])
  const mapRef = useRef<MapRef>(null)
  const isProgrammaticMove = useRef(false)
  const mapLoaded = useRef(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const [open, setOpen] = useState(false)
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)

  const [searchInZoneVisible, setSearchInZoneVisible] = useState(false)

  const mapStyle = 'mapbox://styles/peyce/cl30azfve005k14qnod0isbro'
  const isMobile = useMediaQuery(themeUtils.maxWidth.md)

  const [popupInfo, setPopupInfo] = useState<GeoJSON.Feature<
    GeoJSON.Point,
    AutocompleteItem
  > | null>(null)
  const pins = useMemo(
    () =>
      geoJson?.features?.map((feature) => (
        <Marker
          key={`marker-${feature.id}`}
          longitude={feature.geometry.coordinates[0]!}
          latitude={feature.geometry.coordinates[1]!}
          style={{
            cursor: 'pointer',
            pointerEvents: 'all'
          }}
          onClick={(e) => {
            e.originalEvent.stopPropagation()
            if (!isMobile) {
              isProgrammaticMove.current = true
              mapRef.current?.flyTo({
                center: [
                  feature.geometry.coordinates[0]!,
                  feature.geometry.coordinates[1]!
                ],
                zoom: mapRef.current?.getZoom() || 10,
                duration: 300
              })
              setTimeout(() => {
                isProgrammaticMove.current = false
              }, 400)
            }

            setPopupInfo(feature)
          }}
        >
          <NewFlexContainer>
            <Selected
              id={feature.id}
              onMouseEnter={
                isMobile
                  ? undefined
                  : () => {
                      if (highLightEvent) {
                        highLightEvent(Number(feature.id))
                      }
                    }
              }
              onMouseLeave={
                isMobile
                  ? undefined
                  : () => {
                      if (highLightEvent) {
                        highLightEvent(null)
                      }
                    }
              }
            />
          </NewFlexContainer>
        </Marker>
      )),
    [geoJson]
  )

  const bounds = useMemo<[number, number, number, number] | null>(() => {
    if (!mapLoaded.current && geoJson.features.length === 0) {
      mapLoaded.current = true
      return [-3.954726, 45.3870055, 6.2740665, 51.015681]
    }
    mapLoaded.current = true
    return calculateMapBounds(geoJson.features)
  }, [geoJson.features])

  useEffect(() => {
    if (mapRef.current && bounds) {
      isProgrammaticMove.current = true
      mapRef.current.fitBounds(bounds, {
        padding: 50,
        duration: 200,
        maxZoom: 13
      })

      setTimeout(() => {
        isProgrammaticMove.current = false
      }, 300)
    }
  }, [bounds, mapRef])

  const { refine: clear } = useClearRefinements({
    includedAttributes: ['eventLocationData']
  })

  const handleBoundsSearch = () => {
    if (!mapRef.current) return

    const bounds = mapRef.current.getBounds()
    if (!bounds) return

    const northEast = bounds.getNorthEast()
    const southWest = bounds.getSouthWest()

    clear()
    refineGeoLocation({ northEast, southWest })
    setSearchInZoneVisible(false)
  }

  const handleGeolocateSearch = () => {
    navigator.geolocation.getCurrentPosition((position) => {
      const { latitude, longitude } = position.coords
      mapRef.current?.flyTo({
        center: [longitude, latitude],
        zoom: 15,
        duration: 2000
      })
    })
  }

  const handleZoomIn = () => {
    mapRef.current?.zoomIn({ duration: 500 })
  }
  const handleZoomOut = () => {
    mapRef.current?.zoomOut({ duration: 500 })
  }

  useLanguageSetter(mapRef)
  usePopupHandler({ geoJson, selectedEventId, setPopupInfo, mapRef })

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const observer = new ResizeObserver(() => {
      isProgrammaticMove.current = true
      mapRef.current?.resize()
      setTimeout(() => {
        isProgrammaticMove.current = false
      }, 300)
    })

    observer.observe(container)

    return () => {
      observer.unobserve(container)
    }
  }, [])

  useEffect(() => {
    if (isMobile) {
      document.body.classList.add('no-scroll')
    }

    return () => {
      document.body.classList.remove('no-scroll')
    }
  }, [isMobile])

  return (
    <MapContainer ref={containerRef}>
      {isMobile && (
        <>
          <Stack
            height={122}
            p={2}
            pb={1}
            gap={1}
            zIndex={2000}
            bgcolor={unifiedColors.surface.subdued}
          >
            <SearchBar />
            <FiltersContainer
              flexDirection="row"
              gap={1}
              alignItems="center"
              justifyContent="space-between"
            >
              <Filters open={open} setOpen={setOpen} />
              <SortBy />
            </FiltersContainer>
          </Stack>
          <EventsDrawer open={isDrawerOpen} setOpen={setIsDrawerOpen}>
            <EventsInfiniteScroll closeDrawer={() => setIsDrawerOpen(false)} />
          </EventsDrawer>
        </>
      )}
      <ReactMapGL
        ref={mapRef}
        initialViewState={{
          bounds: bounds || undefined,
          fitBoundsOptions: {
            padding: 50
          }
        }}
        onMoveEnd={() => {
          if (!isProgrammaticMove.current) {
            setSearchInZoneVisible(true)
          }
        }}
        onMoveStart={() => {
          if (!isProgrammaticMove.current) {
            setSearchInZoneVisible(false)
          }
        }}
        mapboxAccessToken={NEXT_PUBLIC_MAPBOX_TOKEN}
        mapStyle={mapStyle}
        locale={flattenObject(t('mapbox', { returnObjects: true }))}
        style={{
          width: '100%',
          height: isMobile ? 'calc(100vh - 122px)' : '100vh'
        }}
        attributionControl={false}
        cooperativeGestures={!isMobile}
        onLoad={onLoad}
      >
        {pins && pins.length > 0 && pins}
        {popupInfo && (
          <EventPopup
            key={popupInfo.id}
            popupInfo={popupInfo}
            setPopupInfo={setPopupInfo}
          />
        )}
        <CrosshairsButton
          variant={ButtonVariant.Quaternary}
          icon={<Crosshairs />}
          onClick={handleGeolocateSearch}
        />
        <SearchInZoneButtonContainer $searchInZoneVisible={searchInZoneVisible}>
          <Button
            label={t('common:map.searchInZone')}
            variant={ButtonVariant.Quaternary}
            onClick={handleBoundsSearch}
          />
        </SearchInZoneButtonContainer>
        {isMobile ? (
          <HideMapButton
            variant={ButtonVariant.Quaternary}
            onClick={hideMap}
            icon={<ArrowLeft />}
          />
        ) : (
          <ExpandButton
            variant={ButtonVariant.Quaternary}
            onClick={() => setExpandedMap((prev) => !prev)}
            icon={expandedMap ? <Reduce /> : <Expand />}
          />
        )}
        <ZoomInButton
          size="small"
          variant={ButtonVariant.Quaternary}
          icon={<Plus />}
          onClick={handleZoomIn}
        />
        <ZoomOutButton
          variant={ButtonVariant.Quaternary}
          icon={<Minus />}
          onClick={handleZoomOut}
        />
      </ReactMapGL>
    </MapContainer>
  )
}
